<template>
  <div id="transitionTarget" class="homePage">
    <div class="videoSection" data-color="blue">
      <BackgroundVideo :is-narrow-viewport="isNarrowViewport" name="hero" />
      <div class="heroSectionContent">
        <HeroSection />
      </div>
    </div>
    <PartnersBar />
    <div class="panel" data-color="blue">
      <ExploreSection />
    </div>
    <div class="panel" data-color="black">
      <FeaturesFirstSection :is-narrow-viewport="isNarrowViewport" />
    </div>
    <div class="panel" data-color="black">
      <FeaturesSecondSection :is-narrow-viewport="isNarrowViewport" />
    </div>
    <div class="panel" data-color="red">
      <DistributionSection />
    </div>
    <div class="panel" data-color="white">
      <StatisticsSection />
    </div>
    <div class="videoSection">
      <BackgroundVideo :is-narrow-viewport="isNarrowViewport" name="monetization" />
      <div class="monetizeSectionContent">
        <MonetizeSection />
      </div>
    </div>
    <div class="panel" data-color="yellow">
      <FaqSection />
    </div>
    <TrustedPartnersSection />
  </div>
</template>

<script setup>
import { useMediaQuery } from '@vueuse/core'
import { onMounted, onUnmounted, watch } from 'vue'

import BackgroundVideo from 'components/pages/home/BackgroundVideo/BackgroundVideo'
import DistributionSection from 'components/pages/home/distributionSection/DistributionSection'
import ExploreSection from 'components/pages/home/exploreSection/ExploreSection'
import FaqSection from 'components/pages/home/faqSection/FaqSection'
import FeaturesFirstSection from 'components/pages/home/featuresFirstSection/FeaturesFirstSection'
import FeaturesSecondSection from 'components/pages/home/featuresSecondSection/FeaturesSecondSection'
import HeroSection from 'components/pages/home/heroSection/HeroSection'
import MonetizeSection from 'components/pages/home/monetizeSection/MonetizeSection'
import StatisticsSection from 'components/pages/home/statisticsSection/StatisticsSection'
import TrustedPartnersSection from 'components/pages/home/trustedPartnersSection/TrustedPartnersSection'
import PartnersBar from 'components/reused-components/partnersBar/PartnersBar'

const isNarrowViewport = useMediaQuery('(max-width: 767px)')
const loadVideos = (timeout = 0) => {
  // as soon as the video can play through, start playback and begin loading the next one
  const videos = document.querySelectorAll('video')
  videos.forEach((video, index) => {
    if (index === 0) video.load()
    video.addEventListener(
      'canplaythrough',
      () => {
        video.play()

        if (index < videos.length - 1) {
          setTimeout(() => videos[index + 1].load(), timeout)
        }
      },
      { once: true }
    )
  })
}

watch(isNarrowViewport, loadVideos)

const observerOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 0.5,
}

const observerCallback = (entries) => {
  entries.forEach((entry) => {
    const sectionColor = entry.target.dataset.color
    const bodyTarget = document.getElementById('transitionTarget')

    if (entry.isIntersecting && sectionColor !== undefined) {
      const colorPrefix = 'color-'
      const classes = bodyTarget.className.split(' ').filter((className) => !className.startsWith(colorPrefix))
      bodyTarget.className = classes.join(' ').trim()
      bodyTarget.classList.add(`${colorPrefix}${sectionColor}`)
    }
  })
}

const PanelsObserver = new IntersectionObserver(observerCallback, observerOptions)

onMounted(() => {
  loadVideos(1000)

  const panelsList = document.querySelectorAll('.panel')
  panelsList.forEach((panel) => {
    PanelsObserver.observe(panel)
  })
})

onUnmounted(() => {
  PanelsObserver.disconnect()
})
</script>

<style lang="scss" scoped>
@import 'assets/scss/base/bootstrap_variables';
@import 'assets/scss/base/functions';

.homePage {
  font-family: $font-family-barlow;
  font-weight: 400;
  line-height: 1;
  color: $color-white;
  background-color: $color-informative-blue;
  background-image: url('/static/images/home/frame-pattern.svg');
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-position: top;
  background-size: cover;
  transition: background-color 1.5s ease-out;

  .videoSection {
    position: relative;

    .heroSectionContent {
      position: absolute;
      top: 10vh;
      left: 50%;
      z-index: 10;
      width: 100%;
      max-width: 1264px;
      min-height: auto;
      padding: 0 16px;
      transform: translateX(-50%);

      @media (min-width: $screen-sm-min) {
        top: unset;
        bottom: 80px;
        padding: 0 32px;
      }

      @media (min-width: $screen-xl-min) {
        left: 110px;
        max-width: unset;
        transform: unset;
      }
    }

    .monetizeSectionContent {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 10;
      width: 100%;
      min-height: auto;
      padding: 12px;
      transform: translate(-50%, -50%);
    }
  }

  .panel {
    display: flex;
    min-height: calc(100vh - 72px);

    @media (min-width: $screen-sm-min) {
      min-height: calc(100vh - 96px);
    }
  }
}

// eslint-disable vue-scoped-css/no-unused-selector
.color-blue {
  background-color: $color-informative-blue;
}

.color-black {
  background-color: $color-black;
}

.color-red {
  background-color: $brand-orange;
}

.color-white {
  background-color: $color-white;
}

.color-yellow {
  background-color: $brand-yellow;
}

// eslint-enable vue-scoped-css/no-unused-selector

:deep(video) {
  pointer-events: none;
}

:deep(.keen-slider) {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}
</style>



// WEBPACK FOOTER //
// Home.vue